import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { businessSupplierService, businessPublicService, businessMappingService } from '../../../services/businessApi';

const emptyForm = { name: '', vendorCode: '', contactPerson: '', contactEmail: '', contactPhone: '', notes: '' };

// Single form for both creating a brand-new Supplier (the Suppliers page's canonical
// entry point) and editing an existing one — pass `supplier` to edit, omit it to create.
// On create, also lets the plants get picked up front so a new supplier doesn't have to
// be re-opened on the detail page just to map it to a plant.
export default function SupplierFormModal({ supplier, onClose, onSaved }) {
  const isEdit = !!supplier;
  const [form, setForm] = useState(supplier ? {
    name: supplier.name || '',
    vendorCode: supplier.vendorCode || '',
    contactPerson: supplier.contactPerson || '',
    contactEmail: supplier.contactEmail || '',
    contactPhone: supplier.contactPhone || '',
    notes: supplier.notes || '',
  } : emptyForm);
  const [plants, setPlants] = useState([]);
  const [selectedPlantIds, setSelectedPlantIds] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) return;
    businessPublicService.listPlants().then(res => setPlants(res.data.plants));
  }, [isEdit]);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const togglePlant = (plantId) => {
    setSelectedPlantIds(ids => ids.includes(plantId) ? ids.filter(id => id !== plantId) : [...ids, plantId]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!isEdit && !form.vendorCode.trim()) { setError('Vendor code is required'); return; }

    setSaving(true);
    try {
      if (isEdit) {
        await businessSupplierService.update(supplier._id, form);
      } else {
        const res = await businessSupplierService.create(form);
        const newSupplierId = res.data.supplier._id;
        for (const plantId of selectedPlantIds) {
          await businessMappingService.create({ plantId, supplierId: newSupplierId });
        }
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="client-form-modal" style={{ margin: 'auto', width: 460, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Supplier' : 'Add Supplier'}</h3>
          <button className="icon-btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <form className="client-form" onSubmit={handleSave}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input value={form.name} onChange={update('name')} required />
            </div>
            <div className="form-group">
              <label>Vendor Code {!isEdit && '*'}</label>
              <input value={form.vendorCode} onChange={update('vendorCode')} required={!isEdit} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact person</label>
              <input value={form.contactPerson} onChange={update('contactPerson')} />
            </div>
            <div className="form-group">
              <label>Contact phone</label>
              <input value={form.contactPhone} onChange={update('contactPhone')} />
            </div>
          </div>

          <div className="form-group">
            <label>Contact email</label>
            <input type="email" value={form.contactEmail} onChange={update('contactEmail')} />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea rows={2} value={form.notes} onChange={update('notes')} />
          </div>

          {!isEdit && (
            <div className="form-group">
              <label>Plants (optional)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border-color, #ddd)', borderRadius: 6, padding: 8 }}>
                {plants.map(p => (
                  <label key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal' }}>
                    <input
                      type="checkbox"
                      checked={selectedPlantIds.includes(p._id)}
                      onChange={() => togglePlant(p._id)}
                    />
                    {p.name} ({p.code})
                  </label>
                ))}
                {plants.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No plants configured yet</span>}
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Onboarding checklists get created automatically for each plant picked. More plants can be added later from the supplier's detail page.
              </p>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
